import useSWR, { mutate } from 'swr';
import useSWRMutation from 'swr/mutation';
import {
    AddSubjectReviewDto,
    RemoveSubjectsFromReviewDto,
    ReviewSubjectDto,
    SetSubjectMonitoringReviewInstanceReviewed,
} from '@interfaces-clean/subject-monitoring/subject-review';
import SubjectMonitoringService from '@pages-clean/subject-monitoring/subject-hub/subject-monitoring.service';
import { SubjectReviewActivityLogApi } from '@apis/bff-api/subject-monitoring/subject-review-activity-log';
import { getData, postData } from '@apis/rest-client';

const subjectReviewUrl = 'SubjectMonitoring/SubjectReview';
const timeout = 30000;

export const SubjectReviewInstanceApiKeys = {
    useGetSubjects: 'GetSubjectReviewInstancesQuery',
} as const;

export const SubjectReviewInstanceApi = {
    invalidateGetAllSubjects: () => {
        // Invalidate `useGetSubjects` query (and update observable)
        // eslint-disable-next-line no-void
        void mutate((key) => Array.isArray(key) && key[0] === SubjectReviewInstanceApiKeys.useGetSubjects, undefined);
        SubjectMonitoringService.refreshSubjectsInEditReviewPanel();
    },

    usePostReview: (studyRef: string) => {
        const postUrl = `${subjectReviewUrl}/Add/${studyRef}`;

        return useSWRMutation(postUrl, async (url: string, { arg }: { arg: AddSubjectReviewDto }) => {
            const result = await postData<AddSubjectReviewDto, boolean>(url, {
                arg,
                timeout,
            });

            SubjectReviewInstanceApi.invalidateGetAllSubjects();
            SubjectReviewActivityLogApi.invalidateGetActivityEntries();

            return result;
        });
    },

    useRemoveFromReview: (studyRef: string) => {
        const postUrl = `${subjectReviewUrl}/Remove/${studyRef}`;

        return useSWRMutation(postUrl, async (url: string, { arg }: { arg: RemoveSubjectsFromReviewDto }) => {
            const result = await postData<RemoveSubjectsFromReviewDto, boolean>(url, { arg, timeout });

            SubjectReviewInstanceApi.invalidateGetAllSubjects();
            SubjectReviewActivityLogApi.invalidateGetActivityEntries();

            return result;
        });
    },

    useGetSubjects: (studyRef: string, reviewRecordId?: number) => {
        const getUrl = reviewRecordId ? [SubjectReviewInstanceApiKeys.useGetSubjects, studyRef, reviewRecordId] : null;
        return useSWR(getUrl, () => {
            const url = `${subjectReviewUrl}/${studyRef}/${reviewRecordId}/Subjects`;
            return getData<ReviewSubjectDto[]>(url, { timeout });
        });
    },

    useSetSubjectReviewInstanceReviewedMutation: (studyRef: string) => {
        const postUrl = `${subjectReviewUrl}/SetReviewed/${studyRef}`;

        return useSWRMutation(postUrl, async (url, { arg }: { arg: SetSubjectMonitoringReviewInstanceReviewed }) => {
            const result = await postData<SetSubjectMonitoringReviewInstanceReviewed, boolean>(url, {
                arg,
                timeout,
            });

            SubjectReviewInstanceApi.invalidateGetAllSubjects();
            SubjectReviewActivityLogApi.invalidateGetActivityEntries();

            return result;
        });
    },
} as const;
